'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../hooks/queries/queryKeys';
import { toast } from 'sonner';
import { ChevronDown, ChevronLeft, MoreHorizontal } from 'lucide-react';
import { cn } from '../lib/cn';
import { getAuthSession } from '../lib/authSession';
import {
  getRunnerScheduleTargetFailures,
  type RunnerSchedule,
  type RunnerScheduleInput,
  type RunnerScheduleRun,
  type RunnerScheduleTargetFailure,
  type RunnerScheduleTargetInput,
} from '../lib/api/runnerSchedule';
import { fetchRunnerModelsForDaemon } from '../lib/api/runnerModels';
import {
  buildTargetRows,
  intersectEngines,
  intersectModelOptions,
  selectAllTargets,
} from '../lib/runnerScheduleTargets';
import { runnerTypeValues, type RunnerType } from '../lib/api';
import { resolveEffectiveEngines } from '../lib/daemonEngines';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { useAgentConfigs } from '../hooks/queries/useAgentConfigs';
import { useDaemons, useSharedDaemons } from '../hooks/queries/useDaemons';
import { useRunnerModelsForDaemon } from '../hooks/queries/useRunnerModels';
import { webRunnerRequestDataAdapter } from '../hooks/useWebRunnerRequestDataAdapter';
import {
  useRunnerSchedules,
  useRunnerScheduleRuns,
  useCreateRunnerScheduleMutation,
  useUpdateRunnerScheduleMutation,
  useSetRunnerScheduleEnabledMutation,
  useDeleteRunnerScheduleMutation,
  useRunRunnerScheduleMutation,
} from '../hooks/queries/useRunnerSchedules';
import { Select } from './Select';
import { Spinner } from './loading/Spinner';
import { TiptapEditor } from './TiptapEditor';
import { getRunnerTypeLabel, hasContent } from './daemonTriggerDetailUtils';
import DeleteConfirmModal from './DeleteConfirmModal';
import { EffortSlider } from './EffortSlider';
import { getEffortLevelLabel, getSupportedEffortLevels } from '../lib/runnerEffort';
import { getBranchFailureReason, shouldFallbackToManualBranchInput } from '../hooks/useDaemonTriggerModal';

const resultChipClass: Record<RunnerScheduleRun['status'], string> = {
  DISPATCHED: 'bg-[var(--chip-success-bg)] text-[var(--chip-success-fg)]',
  SKIPPED: 'bg-[var(--chip-warning-bg)] text-[var(--chip-warning-fg)]',
  FAILED: 'bg-[var(--chip-error-bg)] text-[var(--chip-error-fg)]',
};

// 다중 대상 도입 전 런은 targetDaemonId가 없어 러너 이름을 붙일 수 없다. 그 행은 이름 없이 그대로 둔다.
function RunResult({ run, runnerName }: { run: RunnerScheduleRun; runnerName?: string }) {
  const t = useTranslations('workspace.page.schedules');
  const reasonKey =
    {
      PREVIOUS_RUN_ACTIVE: 'previousRunActive',
      TARGET_UNAVAILABLE: 'targetUnavailable',
      ENGINE_UNAVAILABLE: 'engineUnavailable',
      DISPATCH_ERROR: 'dispatchError',
    }[run.reason ?? ''] ?? 'unknownReason';
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', resultChipClass[run.status])}>
        {t(`results.${run.status}`)}
      </span>
      {runnerName && <span className="min-w-0 truncate text-xs font-medium">{runnerName}</span>}
      {run.reason && <span className="text-xs text-[var(--color-fg-muted)]">{t(`reasons.${reasonKey}`)}</span>}
    </span>
  );
}

function ScheduleRow({
  schedule,
  token,
  canEdit,
  agentName,
  runnerNames,
  onEdit,
  onDelete,
}: {
  schedule: RunnerSchedule;
  token: string | null;
  canEdit: boolean;
  agentName: string | undefined;
  runnerNames: Map<string, string>;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const t = useTranslations('workspace.page.schedules');
  const c = useTranslations('common');
  const layout = useTranslations('components.appLayout');
  const locale = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);
  // 최신 회차 요약을 위해 대상 상한(20개)만큼 읽는다. 펼친 이력은 10개씩 둔다.
  const recent = useRunnerScheduleRuns(token, schedule.projectId, schedule.id, 1, 20);
  const history = useRunnerScheduleRuns(token, schedule.projectId, expanded && page > 1 ? schedule.id : '', page);
  const runs = page === 1 ? recent : history;
  const toggle = useSetRunnerScheduleEnabledMutation(token, schedule.projectId);
  const run = useRunRunnerScheduleMutation(token, schedule.projectId);
  const date = (value: string) =>
    new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: schedule.timezone,
    }).format(new Date(value));
  // hour/minute는 스케줄 타임존 기준 벽시계 값이라 UTC로 고정해 그대로 포맷한다.
  const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(
    Date.UTC(2000, 0, 1, schedule.hour, schedule.minute),
  );
  const rule =
    schedule.frequency === 'WEEKLY' && schedule.weekday != null
      ? t('ruleWeekly', { weekday: t(`weekdays.${schedule.weekday}`), time })
      : t('ruleDaily', { time });
  const showTimezone = schedule.timezone !== browserTimezone();
  const summary = [
    rule,
    showTimezone ? schedule.timezone : null,
    getRunnerTypeLabel(schedule.runnerType, layout),
    agentName,
  ].filter(Boolean);
  const latest = recent.data?.data[0];
  // 다중 대상은 한 회차가 대상 수만큼의 행으로 기록된다. 최신 회차의 행을 묶어 상태별 개수로 요약한다.
  // 대상 상한이 20개라 1페이지(20행)에 최신 회차가 온전히 들어간다.
  const latestBatch = latest
    ? (recent.data?.data ?? []).filter((item) => item.scheduledFor === latest.scheduledFor)
    : [];
  const latestSummary =
    latestBatch.length > 1
      ? t('runPartial', {
          dispatched: latestBatch.filter((item) => item.status === 'DISPATCHED').length,
          skipped: latestBatch.filter((item) => item.status === 'SKIPPED').length,
          failed: latestBatch.filter((item) => item.status === 'FAILED').length,
        })
      : null;
  const runnerNameOf = (item: RunnerScheduleRun) =>
    item.targetDaemonId ? runnerNames.get(item.targetDaemonId) : undefined;
  return (
    <li className="rounded-xl border border-base-200">
      <div className="space-y-3 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className={cn('min-w-0', !schedule.isEnabled && 'opacity-60')}>
            <h3 className="break-words text-sm font-semibold">{schedule.title}</h3>
            <p className="mt-0.5 text-xs text-[var(--color-fg-muted)]">{summary.join(' · ')}</p>
          </div>
          <label className="flex shrink-0 items-center gap-2 text-xs text-[var(--color-fg-muted)]">
            {schedule.isEnabled ? t('enabled') : t('disabled')}
            <input
              type="checkbox"
              className="toggle toggle-sm"
              checked={schedule.isEnabled}
              disabled={!canEdit || toggle.isPending}
              aria-label={t('toggle', { title: schedule.title })}
              onChange={(event) =>
                toggle.mutate(
                  { id: schedule.id, isEnabled: event.target.checked },
                  {
                    onError: () => toast.error(t('saveFailed')),
                  },
                )
              }
            />
          </label>
        </div>
        <dl
          className={cn('grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2', !schedule.isEnabled && 'opacity-60')}
        >
          <div className="min-w-0">
            <dt className="text-xs text-[var(--color-fg-subtle)]">{t('nextRun')}</dt>
            <dd className="mt-0.5">
              {!schedule.isEnabled ? t('paused') : schedule.nextRunAt ? date(schedule.nextRunAt) : t('notScheduled')}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-[var(--color-fg-subtle)]">{t('latestResult')}</dt>
            <dd className="mt-0.5">
              {recent.isPending ? (
                c('loading')
              ) : recent.isError ? (
                t('historyFailed')
              ) : latest ? (
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <time dateTime={latest.scheduledFor}>{date(latest.scheduledFor)}</time>
                  {latestSummary ? (
                    <span className="text-xs text-[var(--color-fg-muted)]">{latestSummary}</span>
                  ) : (
                    <RunResult run={latest} runnerName={runnerNameOf(latest)} />
                  )}
                </span>
              ) : (
                <span className="text-[var(--color-fg-muted)]">{t('neverRun')}</span>
              )}
            </dd>
          </div>
        </dl>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-base-200 px-2 py-1.5">
        <button
          type="button"
          className="btn btn-ghost btn-sm gap-1"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {t('history')}
          <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} aria-hidden="true" />
        </button>
        {canEdit && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={run.isPending}
              onClick={() =>
                run.mutate(schedule.id, {
                  // 대상 수만큼 런이 생긴다. 여러 대면 개수만 알리고 결과는 이력에서 본다.
                  onSuccess: (results) => {
                    const only = results.length === 1 ? results[0] : undefined;
                    if (only) toast(t(`results.${only.status}`));
                    else {
                      // 응답은 발행·건너뜀·실패를 모두 담으므로 상태별로 세어 알린다.
                      const dispatched = results.filter((item) => item.status === 'DISPATCHED').length;
                      const skipped = results.filter((item) => item.status === 'SKIPPED').length;
                      const failed = results.filter((item) => item.status === 'FAILED').length;
                      const summary = t('runPartial', { dispatched, skipped, failed });
                      if (dispatched === results.length) toast(t('runDispatched', { count: dispatched }));
                      else if (dispatched === 0) toast.warning(summary);
                      else toast(summary);
                    }
                  },
                  onError: () => toast.error(t('runFailed')),
                })
              }
            >
              {t('runNow')}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
              {c('actions.edit')}
            </button>
            <div className="dropdown dropdown-end">
              <button
                type="button"
                tabIndex={0}
                className="btn btn-ghost btn-sm btn-square"
                aria-label={t('moreActions', { title: schedule.title })}
                title={t('moreActions', { title: schedule.title })}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </button>
              <ul
                tabIndex={0}
                className="menu dropdown-content z-[1] mt-1 w-32 rounded-box border border-base-300 bg-base-100 p-1 shadow"
              >
                <li>
                  <button type="button" className="text-error" onClick={onDelete}>
                    {c('actions.delete')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
      {expanded && (
        <div className="space-y-2 border-t border-base-200 px-4 py-3">
          {runs.isPending ? (
            <Spinner />
          ) : runs.isError ? (
            <p role="alert" className="text-sm">
              {t('historyFailed')}{' '}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void runs.refetch()}>
                {c('actions.retry')}
              </button>
            </p>
          ) : (
            <>
              {runs.data?.data.length === 0 && <p className="text-sm text-[var(--color-fg-muted)]">{t('noRuns')}</p>}
              <ul className="space-y-2 text-sm">
                {runs.data?.data.map((item) => (
                  <li key={item.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <time dateTime={item.scheduledFor} className="tabular-nums">
                      {date(item.scheduledFor)}
                    </time>
                    <RunResult run={item} runnerName={runnerNameOf(item)} />
                  </li>
                ))}
              </ul>
              {(runs.data?.meta.totalPages ?? 0) > 1 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    {c('actions.prev')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={page >= (runs.data?.meta.totalPages ?? 1)}
                    onClick={() => setPage(page + 1)}
                  >
                    {c('actions.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}

// 서버가 돌려주는 러너별 거절 사유 코드를 안내 문구 키로 옮긴다. 모르는 코드는 일반 사유로 보여준다.
const targetFailureKey: Record<string, string> = {
  TARGET_INVALID: 'targetInvalid',
  ENGINE_NOT_SUPPORTED: 'engineNotSupported',
  ENGINE_NOT_DETECTED: 'engineNotDetected',
  MODEL_INVALID: 'modelInvalid',
  FAST_MODE_UNSUPPORTED: 'fastModeUnsupported',
  EFFORT_UNSUPPORTED: 'effortUnsupported',
};

const fieldLabelClass = 'text-xs font-medium text-[var(--color-fg-muted)]';

function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  const field = (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className={fieldLabelClass}>{label}</span>
      {children}
    </label>
  );
  if (!hint) return field;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {field}
      {hint}
    </div>
  );
}

const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const supportedTimezones = (): string[] => {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
  return intl.supportedValuesOf?.('timeZone') ?? [];
};

function FormSection({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3 border-t border-base-200 pt-5 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h4 className="text-sm font-semibold">{title}</h4>
        {aside}
      </div>
      {children}
    </section>
  );
}

function ScheduleForm({
  token,
  projectId,
  schedule,
  onClose,
}: {
  token: string | null;
  projectId: string;
  schedule: RunnerSchedule | null;
  onClose: () => void;
}) {
  const t = useTranslations('workspace.page.schedules');
  const c = useTranslations('common');
  const layout = useTranslations('components.appLayout');
  const [form, setForm] = useState<RunnerScheduleInput>(() => ({
    title: schedule?.title ?? '',
    prompt: schedule?.prompt ?? { type: 'doc', content: [] },
    frequency: schedule?.frequency ?? 'DAILY',
    weekday: schedule?.weekday ?? 1,
    hour: schedule?.hour ?? 9,
    minute: schedule?.minute ?? 0,
    timezone: schedule?.timezone ?? browserTimezone(),
    isEnabled: schedule?.isEnabled ?? true,
    agentConfigId: schedule?.agentConfigId ?? '',
    targetDaemonId: schedule?.targetDaemonId ?? null,
    targets: schedule?.targets?.length
      ? schedule.targets.map((target) => ({ daemonId: target.daemonId, agentConfigId: target.agentConfigId }))
      : undefined,
    runnerType: schedule?.runnerType ?? 'CODEX',
    model: schedule?.model ?? null,
    effort: schedule?.effort ?? null,
    useWorktree: schedule?.useWorktree ?? false,
    repositoryId: schedule?.repositoryId ?? null,
    baseBranch: schedule?.baseBranch ?? null,
  }));
  const [error, setError] = useState('');
  // 저장된 대상이 있으면 여러 러너 모드로 연다. 단일 러너 폼은 기존 UX 그대로 남는다.
  const [multi, setMulti] = useState(() => (schedule?.targets?.length ?? 0) > 0);
  const [targetFailures, setTargetFailures] = useState<RunnerScheduleTargetFailure[]>([]);
  const [editingTimezone, setEditingTimezone] = useState(false);
  // 목록이 길어 자주 쓰는 값(브라우저 시간대·폼을 연 시점의 값·UTC)을 맨 위에 둔다.
  const [timezoneOptions] = useState(() =>
    [...new Set([browserTimezone(), form.timezone, 'UTC', ...supportedTimezones()])].map((value) => ({
      value,
      label: value,
    })),
  );
  const agents = useAgentConfigs(token, projectId);
  const ownDaemons = useDaemons(token);
  const sharedDaemons = useSharedDaemons(token, projectId);
  const repositories = webRunnerRequestDataAdapter.useRepositories(token, projectId, true);
  const queryClient = useQueryClient();
  const branches = webRunnerRequestDataAdapter.useBranches(
    token,
    projectId,
    form.repositoryId ?? '',
    form.useWorktree ?? false,
  );
  const daemons = [...(ownDaemons.data?.daemons ?? []), ...(sharedDaemons.data ?? [])];
  const effectiveDaemonId =
    form.targetDaemonId ?? agents.data?.find((item) => item.id === form.agentConfigId)?.targetDaemonId;
  const selectedDaemon = daemons.find((item) => item.id === effectiveDaemonId);
  const shared = sharedDaemons.data?.find((item) => item.id === effectiveDaemonId);
  const models = useRunnerModelsForDaemon(token, projectId, effectiveDaemonId ?? '');
  const create = useCreateRunnerScheduleMutation(token, projectId);
  const update = useUpdateRunnerScheduleMutation(token, projectId);
  const pending = create.isPending || update.isPending;
  const set = <K extends keyof RunnerScheduleInput>(key: K, value: RunnerScheduleInput[K]) =>
    setForm((previous) => ({ ...previous, [key]: value }));
  // Runner를 직접 지정하면 그 Runner에 소속된 Agent만 고를 수 있다.
  // 공유 Runner는 소유자가 공유한 Agent, 내 Runner는 해당 Runner에 연결된 Agent가 소속이다.
  const pinnedShared = form.targetDaemonId
    ? sharedDaemons.data?.find((item) => item.id === form.targetDaemonId)
    : undefined;
  const belongsToPinnedRunner = (item: { id: string; targetDaemonId?: string | null }) =>
    !form.targetDaemonId ||
    (pinnedShared
      ? pinnedShared.agentConfigs.some((agent) => agent.id === item.id)
      : item.targetDaemonId === form.targetDaemonId);
  const agentOptions = (agents.isPlaceholderData ? [] : (agents.data ?? []))
    .filter((item) => !item.deletedAt && item.projectId === projectId)
    .filter((item) => !shared || shared.agentConfigs.some((agent) => agent.id === item.id))
    .filter(belongsToPinnedRunner)
    .map((item) => ({ value: item.id, label: item.name }));
  // 수동 지정이 없으면 신선한 자동 탐지 목록을 쓰고, 둘 다 없으면 제한 없음(전체)으로 본다.
  // 단일 경로도 서버 판정과 같은 유효 엔진을 읽는다.
  const singleEffective = selectedDaemon
    ? resolveEffectiveEngines({
        supportedEngines: selectedDaemon.supportedEngines ?? [],
        detectedEngines: selectedDaemon.detectedEngines ?? null,
        detectedEnginesAt: selectedDaemon.detectedEnginesAt ?? null,
      })
    : [];
  const singleEngines = (
    singleEffective.length > 0 ? singleEffective : selectedDaemon ? [...runnerTypeValues] : []
  ).filter((engine) => !shared?.allowedEngines.length || (shared.allowedEngines as readonly string[]).includes(engine));
  const selectPlaceholder = { value: '', label: c('actions.select') };
  // 여러 러너 모드는 공유받은 러너를 쓰지 않는다 — 대상은 내 러너로 한정한다.
  const projectAgents = (agents.isPlaceholderData ? [] : (agents.data ?? [])).filter(
    (item) => !item.deletedAt && item.projectId === projectId,
  );
  const targetRows = buildTargetRows(ownDaemons.data?.daemons ?? [], projectAgents);
  const targets = form.targets ?? [];
  const selectedTargetDaemons = targets.flatMap(
    (target) => targetRows.find((row) => row.daemon.id === target.daemonId)?.daemon ?? [],
  );
  // 기여한 러너가 없으면(자동 탐지 포함 유효 목록이 전부 빔) 제한 없음으로 전체 엔진을 쓴다.
  const multiEngines = intersectEngines(selectedTargetDaemons, { allEngines: runnerTypeValues });
  // 러너별 모델 목록을 단일 러너 경로와 같은 쿼리 키로 병렬 조회해 캐시를 공유한다.
  const targetModelQueries = useQueries({
    queries: (multi ? targets : []).map((target) => ({
      queryKey: queryKeys.runnerModels.forDaemon(projectId, target.daemonId),
      queryFn: () => fetchRunnerModelsForDaemon(token as string, projectId, target.daemonId),
      enabled: Boolean(token),
      staleTime: 300_000,
      gcTime: 1_800_000,
    })),
  });
  const targetModelsReady = targetModelQueries.length > 0 && targetModelQueries.every((query) => query.isSuccess);
  const engines = multi ? multiEngines : singleEngines;
  const modelsReady = multi ? targetModelsReady : models.isSuccess;
  const validRepository =
    !form.useWorktree ||
    (repositories.data ?? []).some(
      (item) => item.id === form.repositoryId && ['GITHUB', 'GITLAB'].includes(item.provider),
    );
  const resourceError =
    agents.isError ||
    ownDaemons.isError ||
    sharedDaemons.isError ||
    repositories.isError ||
    models.isError ||
    targetModelQueries.some((query) => query.isError);
  const validTarget = multi
    ? targets.length > 0 && targets.every((target) => target.agentConfigId) && engines.includes(form.runnerType)
    : Boolean(
        selectedDaemon &&
        agentOptions.some((item) => item.value === form.agentConfigId) &&
        engines.includes(form.runnerType),
      );
  const save = async () => {
    setError('');
    setTargetFailures([]);
    const first = targets[0];
    const input = {
      ...form,
      // 여러 러너 → 한 러너로 되돌린 저장은 targets: []로 보내 저장된 대상 행을 지운다.
      ...(!multi && (schedule?.targets?.length ?? 0) > 0 ? { targets: [] as RunnerScheduleTargetInput[] } : {}),
      // 모델 목록을 받은 뒤에만 지원 레벨로 걸러, 로딩 중 수정 저장이 기존 값을 지우지 않게 한다.
      effort: modelsReady && form.effort && !effortLevels.includes(form.effort) ? null : form.effort,
      title: form.title.trim(),
      weekday: form.frequency === 'DAILY' ? null : form.weekday,
      ...(shared?.planModeOnly ? { planMode: true } : {}),
      // 서버는 첫 대상으로 미러 컬럼을 채우지만 스키마상 두 값이 필수라 함께 보낸다.
      ...(multi && first ? { agentConfigId: first.agentConfigId, targetDaemonId: first.daemonId } : {}),
    };
    try {
      if (!validTarget || !validRepository || pending) return;
      if (schedule) await update.mutateAsync({ id: schedule.id, input });
      else await create.mutateAsync(input);
      onClose();
    } catch (failure) {
      // 웹 교집합은 사전 안내일 뿐이고 최종 판정은 서버다. 거절된 러너를 해당 행에 표시한다.
      setTargetFailures(getRunnerScheduleTargetFailures(failure));
      setError(t('saveFailed'));
    }
  };
  const modelOptions = multi
    ? targetModelsReady
      ? intersectModelOptions(targetModelQueries.map((query) => query.data?.[form.runnerType] ?? []))
      : []
    : (models.data?.[form.runnerType] ?? []);
  const effortLevelsFor = (model: string | null | undefined) =>
    model
      ? getSupportedEffortLevels(
          form.runnerType,
          modelOptions.find((item) => item.value === model)?.supportedEffortLevels,
        )
      : [];
  const effortLevels = effortLevelsFor(form.model);
  const engineKey = engines.join(',');
  const modelKey = modelOptions.map((item) => item.value).join(',');
  // 대상이 바뀌어 현재 엔진·모델이 교집합에서 빠지면 단일 러너를 바꿨을 때와 같이 초기화한다.
  useEffect(() => {
    if (!multi) return;
    const availableEngines = engineKey ? engineKey.split(',') : [];
    const availableModels = modelKey ? modelKey.split(',') : [];
    setForm((previous) => {
      const engineOk = availableEngines.length === 0 || availableEngines.includes(previous.runnerType);
      const modelOk = !previous.model || !targetModelsReady || availableModels.includes(previous.model);
      if (engineOk && modelOk) return previous;
      return {
        ...previous,
        runnerType: engineOk ? previous.runnerType : ((availableEngines[0] ?? previous.runnerType) as RunnerType),
        model: null,
        effort: null,
      };
    });
  }, [multi, engineKey, modelKey, targetModelsReady]);
  const replaceTargets = (next: RunnerScheduleTargetInput[]) => {
    setTargetFailures([]);
    setForm((previous) => ({ ...previous, targets: next }));
  };
  const toggleTarget = (daemonId: string, agentConfigId: string, checked: boolean) =>
    replaceTargets(
      checked ? [...targets, { daemonId, agentConfigId }] : targets.filter((target) => target.daemonId !== daemonId),
    );
  const setTargetAgent = (daemonId: string, agentConfigId: string) =>
    replaceTargets(targets.map((target) => (target.daemonId === daemonId ? { ...target, agentConfigId } : target)));
  const targetFailureMessage = (code: string) => t(`targetFailures.${targetFailureKey[code] ?? 'targetInvalid'}`);
  // 일시적인 조회 오류도 스케줄 저장을 막지 않도록 직접 입력을 허용한다.
  const selectedRepository = repositories.data?.find((item) => item.id === form.repositoryId) ?? null;
  const manualBranch = branches.isError || shouldFallbackToManualBranchInput(selectedRepository, branches.error);
  const branchFailureMessageKey = {
    PROJECT_RECONNECT_REQUIRED: 'daemonBranchProjectReconnectRequired',
    PROJECT_REPOSITORY_FORBIDDEN: 'daemonBranchProjectRepositoryForbidden',
    CONNECTION_REQUIRED: 'daemonBranchConnectionRequired',
    PERSONAL_RECONNECT_REQUIRED: 'daemonBranchPersonalReconnectRequired',
    PERSONAL_REPOSITORY_FORBIDDEN: 'daemonBranchPersonalRepositoryForbidden',
    REMOTE_URL_INVALID: 'daemonBranchRemoteUrlInvalid',
  } as const;
  const branchFailureReason = getBranchFailureReason(branches.error);
  const branchHelp = !form.repositoryId
    ? null
    : selectedRepository?.provider === 'MANUAL'
      ? layout('daemonBranchManualRepositoryHint')
      : branchFailureReason
        ? layout(branchFailureMessageKey[branchFailureReason])
        : branches.isError || (!branches.isFetching && branches.data?.length === 0)
          ? t('branchUnavailable')
          : null;
  const branchNames = (branches.data ?? []).map((branch) => branch.name);
  const branchOptions = [
    { value: '', label: branches.isFetching ? layout('daemonBranchLoading') : c('actions.select') },
    // 저장된 브랜치가 목록에 없어도(삭제·로딩 전) 선택값이 사라지지 않게 유지한다.
    ...(form.baseBranch && !branchNames.includes(form.baseBranch) ? [form.baseBranch] : []),
    ...branchNames,
  ].map((item) => (typeof item === 'string' ? { value: item, label: item } : item));
  const engineOptions = engines.length
    ? engines.map((value) => ({ value, label: getRunnerTypeLabel(value, layout) }))
    : [{ value: form.runnerType, label: multi ? t('chooseRunnersFirst') : t('chooseAgentFirst') }];
  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-square -ml-2"
          aria-label={c('actions.back')}
          title={c('actions.back')}
          disabled={pending}
          onClick={onClose}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <h3 className="text-sm font-semibold">{schedule ? t('editSchedule') : t('addSchedule')}</h3>
      </div>
      <fieldset disabled={pending} className="space-y-5">
        <FormSection title={t('sectionTask')}>
          <Field label={c('fields.title')}>
            <input
              className="input input-bordered input-sm w-full"
              required
              maxLength={255}
              value={form.title}
              onChange={(event) => set('title', event.target.value)}
            />
          </Field>
          <div className="flex flex-col gap-1.5">
            <span id="schedule-prompt-label" className={fieldLabelClass}>
              {t('prompt')}
            </span>
            <div
              role="group"
              aria-labelledby="schedule-prompt-label"
              className="min-h-24 rounded-field border border-base-content/20 text-sm focus-within:border-primary"
            >
              <TiptapEditor value={form.prompt} onChangeAction={(value) => set('prompt', value)} compact hideToolbar />
            </div>
          </div>
        </FormSection>
        <FormSection
          title={t('sectionTiming')}
          aside={
            !editingTimezone && (
              <p className="flex items-center gap-x-2 text-xs text-[var(--color-fg-muted)]">
                {t('timezoneBasis', { timezone: form.timezone })}
                <button
                  type="button"
                  className="link link-hover text-[var(--color-primary)]"
                  aria-label={t('changeTimezone')}
                  onClick={() => setEditingTimezone(true)}
                >
                  {t('change')}
                </button>
              </p>
            )
          }
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('frequency')}>
              <Select
                ariaLabel={t('frequency')}
                value={form.frequency}
                options={['DAILY', 'WEEKLY'].map((value) => ({ value, label: t(value) }))}
                onChange={(value) => set('frequency', value as RunnerScheduleInput['frequency'])}
                fullWidth
              />
            </Field>
            {form.frequency === 'WEEKLY' && (
              <Field label={t('weekday')}>
                <Select
                  ariaLabel={t('weekday')}
                  value={String(form.weekday)}
                  options={Array.from({ length: 7 }, (_, day) => ({ value: String(day), label: t(`weekdays.${day}`) }))}
                  onChange={(value) => set('weekday', Number(value))}
                  fullWidth
                />
              </Field>
            )}
            <Field label={t('time')}>
              <input
                type="time"
                className="input input-bordered input-sm w-full"
                required
                value={`${String(form.hour).padStart(2, '0')}:${String(form.minute).padStart(2, '0')}`}
                onChange={(event) => {
                  const [hour, minute] = event.target.value.split(':').map(Number);
                  setForm((previous) => ({ ...previous, hour, minute }));
                }}
              />
            </Field>
            {editingTimezone && (
              <Field label={t('timezone')}>
                <Select
                  ariaLabel={t('timezone')}
                  value={form.timezone}
                  options={timezoneOptions}
                  onChange={(value) => set('timezone', value)}
                  fullWidth
                />
              </Field>
            )}
          </div>
        </FormSection>
        <FormSection title={t('sectionEnvironment')}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('targetMode')}>
              <Select
                ariaLabel={t('targetMode')}
                value={multi ? 'multi' : 'single'}
                options={[
                  { value: 'single', label: t('targetModeSingle') },
                  { value: 'multi', label: t('targetModeMulti') },
                ]}
                onChange={(value) => {
                  setMulti(value === 'multi');
                  setTargetFailures([]);
                  setForm((previous) => ({
                    ...previous,
                    targets: value === 'multi' ? [] : undefined,
                    targetDaemonId: null,
                    agentConfigId: '',
                    model: null,
                    effort: null,
                  }));
                }}
                fullWidth
              />
            </Field>
            {multi ? (
              <div className="flex min-w-0 flex-col gap-2 sm:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={fieldLabelClass}>{t('targetRunners')}</span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs"
                    onClick={() => replaceTargets(selectAllTargets(targetRows, targets))}
                  >
                    {t('selectAllRunners')}
                  </button>
                </div>
                {targetRows.length === 0 ? (
                  <p className="text-xs text-[var(--color-fg-muted)]">{t('noOwnRunners')}</p>
                ) : (
                  <ul className="space-y-2">
                    {targetRows.map((row) => {
                      const label = row.daemon.label ?? row.daemon.id;
                      const selected = targets.find((target) => target.daemonId === row.daemon.id);
                      const firstAgent = row.agents[0];
                      const failure = targetFailures.find((item) => item.daemonId === row.daemon.id);
                      return (
                        <li key={row.daemon.id} className="rounded-xl border border-base-200 px-3 py-2">
                          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                            <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                className="checkbox checkbox-sm shrink-0"
                                checked={Boolean(selected)}
                                disabled={!firstAgent}
                                aria-label={label}
                                onChange={(event) =>
                                  firstAgent &&
                                  toggleTarget(
                                    row.daemon.id,
                                    selected?.agentConfigId ?? firstAgent.id,
                                    event.target.checked,
                                  )
                                }
                              />
                              <span className="min-w-0 flex-1 truncate">{label}</span>
                            </label>
                            {selected && firstAgent && (
                              <Select
                                ariaLabel={t('targetAgent', { runner: label })}
                                value={selected.agentConfigId}
                                options={row.agents.map((agent) => ({ value: agent.id, label: agent.name }))}
                                onChange={(value) => setTargetAgent(row.daemon.id, value)}
                              />
                            )}
                          </div>
                          {!firstAgent && (
                            <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{t('runnerHasNoAgent')}</p>
                          )}
                          {failure && <p className="mt-1 text-xs text-error">{targetFailureMessage(failure.code)}</p>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : (
              <>
                <Field
                  label={t('runner')}
                  hint={
                    !form.targetDaemonId &&
                    form.agentConfigId &&
                    (selectedDaemon ? (
                      <p className="text-xs text-[var(--color-fg-muted)]">
                        {t('resolvedRunner', { runner: selectedDaemon.label ?? selectedDaemon.id })}
                      </p>
                    ) : (
                      <p className="text-xs text-error">{t('agentRunnerMissing')}</p>
                    ))
                  }
                >
                  <Select
                    ariaLabel={t('runner')}
                    value={form.targetDaemonId ?? ''}
                    options={[
                      { value: '', label: t('agentRunner') },
                      ...daemons.map((item) => ({ value: item.id, label: item.label ?? item.id })),
                    ]}
                    onChange={(value) =>
                      setForm((previous) => ({
                        ...previous,
                        targetDaemonId: value || null,
                        agentConfigId: '',
                        model: null,
                        effort: null,
                      }))
                    }
                    fullWidth
                  />
                </Field>
                <Field label={t('agent')}>
                  <Select
                    ariaLabel={t('agent')}
                    value={form.agentConfigId}
                    options={[selectPlaceholder, ...agentOptions]}
                    onChange={(value) =>
                      setForm((previous) => ({ ...previous, agentConfigId: value, model: null, effort: null }))
                    }
                    fullWidth
                  />
                </Field>
              </>
            )}
            <Field
              label={layout('runnerType')}
              hint={
                multi &&
                targets.length > 0 &&
                engines.length === 0 && <p className="text-xs text-error">{t('noCommonEngine')}</p>
              }
            >
              <Select
                ariaLabel={layout('runnerType')}
                value={form.runnerType}
                options={engineOptions}
                disabled={!engines.length}
                onChange={(value) =>
                  setForm((previous) => ({ ...previous, runnerType: value as RunnerType, model: null, effort: null }))
                }
                fullWidth
              />
            </Field>
            <div className="relative min-w-0">
              <Field label={layout('runnerModel')}>
                <Select
                  ariaLabel={layout('runnerModel')}
                  value={form.model ?? ''}
                  options={[
                    { value: '', label: layout('runnerModelClientDefault') },
                    ...modelOptions.map((item) => ({ value: item.value, label: item.label })),
                  ]}
                  // 여러 러너 모드는 대상 전부의 모델 목록을 받아야 교집합을 알 수 있다.
                  disabled={multi ? !targetModelsReady : !selectedDaemon}
                  onChange={(value) =>
                    setForm((previous) => ({
                      ...previous,
                      model: value || null,
                      // 새 모델이 지원하지 않는 강도는 남기지 않는다.
                      effort:
                        previous.effort && effortLevelsFor(value).includes(previous.effort) ? previous.effort : null,
                    }))
                  }
                  fullWidth
                />
              </Field>
              {effortLevels.length > 0 && (
                <EffortSlider
                  className="absolute right-0 top-0 -translate-y-0.5"
                  id="schedule-effort-select"
                  value={form.effort ?? ''}
                  onChange={(value) => set('effort', value || null)}
                  options={[
                    { value: '', label: layout('daemonEffortModelDefault') },
                    ...effortLevels.map((level) => ({ value: level, label: getEffortLevelLabel(level, layout) })),
                  ]}
                  title={layout('runnerEffort')}
                  autoLabel={layout('daemonEffortAuto')}
                  compactAutoLabel={layout('daemonEffortDefaultShort')}
                  fasterLabel={layout('daemonEffortFaster')}
                  smarterLabel={layout('daemonEffortSmarter')}
                />
              )}
            </div>
          </div>
          <div className="rounded-xl border border-base-200 px-4 py-3">
            <label className="flex items-start justify-between gap-3">
              <span className="min-w-0">
                <span className="block text-sm font-medium">{layout('useWorktreeLabel')}</span>
                <span className="mt-0.5 block text-xs text-[var(--color-fg-muted)]">{t('worktreeHint')}</span>
              </span>
              <input
                type="checkbox"
                className="toggle toggle-sm mt-0.5 shrink-0"
                checked={form.useWorktree}
                onChange={(event) => set('useWorktree', event.target.checked)}
              />
            </label>
            {form.useWorktree && (
              <div className="mt-3 grid grid-cols-1 gap-3 border-t border-base-200 pt-3 sm:grid-cols-2">
                <Field label={layout('daemonRepository')}>
                  <Select
                    ariaLabel={layout('daemonRepository')}
                    value={form.repositoryId ?? ''}
                    options={[
                      selectPlaceholder,
                      ...(repositories.data ?? [])
                        .filter((item) => ['GITHUB', 'GITLAB'].includes(item.provider))
                        .map((item) => ({ value: item.id, label: item.name })),
                    ]}
                    onChange={(value) =>
                      setForm((previous) => ({ ...previous, repositoryId: value || null, baseBranch: null }))
                    }
                    fullWidth
                  />
                </Field>
                <Field
                  label={t('baseBranch')}
                  hint={
                    branchHelp && (
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xs text-[var(--color-fg-muted)]">{branchHelp}</p>
                        {branches.isError && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            disabled={branches.isFetching}
                            onClick={() =>
                              void queryClient.refetchQueries({
                                queryKey: queryKeys.projects.repositoryBranches(projectId, form.repositoryId ?? ''),
                                exact: true,
                              })
                            }
                          >
                            {c('actions.retry')}
                          </button>
                        )}
                      </div>
                    )
                  }
                >
                  {manualBranch ? (
                    <input
                      className="input input-bordered input-sm w-full"
                      placeholder={layout('daemonBranchManualPlaceholder')}
                      value={form.baseBranch ?? ''}
                      onChange={(event) => set('baseBranch', event.target.value || null)}
                    />
                  ) : (
                    <Select
                      ariaLabel={t('baseBranch')}
                      value={form.baseBranch ?? ''}
                      options={branchOptions}
                      disabled={!form.repositoryId}
                      onChange={(value) => set('baseBranch', value || null)}
                      fullWidth
                    />
                  )}
                </Field>
              </div>
            )}
          </div>
        </FormSection>
      </fieldset>
      {resourceError && (
        <p role="alert" className="text-sm text-error">
          {t('environmentFailed')}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-error">
          {error}
        </p>
      )}
      <div className="sticky bottom-[-1.25rem] -mx-5 -mb-5 flex flex-wrap items-center justify-between gap-3 border-t border-base-200 bg-base-100 px-5 py-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="toggle toggle-sm"
            checked={form.isEnabled}
            disabled={pending}
            onChange={(event) => set('isEnabled', event.target.checked)}
          />
          {t('enableOnSave')}
        </label>
        <div className="flex flex-wrap items-center gap-2">
          {multi && targets.length > 0 && (
            <span className="text-xs text-[var(--color-fg-muted)]">
              {t('dispatchCount', { count: targets.length })}
            </span>
          )}
          <button type="button" className="btn btn-ghost btn-sm" disabled={pending} onClick={onClose}>
            {c('actions.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={
              pending ||
              !validRepository ||
              !form.title.trim() ||
              !hasContent(form.prompt) ||
              !validTarget ||
              Boolean(form.useWorktree && (!form.repositoryId || !form.baseBranch?.trim()))
            }
          >
            {pending ? <Spinner /> : c('actions.save')}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function RunnerScheduleSettingsView({ projectId }: { projectId: string }) {
  const t = useTranslations('workspace.page.schedules');
  const c = useTranslations('common');
  const token = useMemo(() => getAuthSession()?.token ?? null, []);
  const { can } = useProjectPermissions({ projectId });
  const canEdit = can('runners.write');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<RunnerSchedule | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<RunnerSchedule | null>(null);
  const list = useRunnerSchedules(token, projectId, page, can('runners.read'));
  const remove = useDeleteRunnerScheduleMutation(token, projectId);
  const agents = useAgentConfigs(token, projectId);
  const agentNames = new Map((agents.data ?? []).map((item) => [item.id, item.name]));
  // 실행 이력에 러너 이름을 붙이려면 내 러너와 공유 러너 라벨이 모두 필요하다.
  const ownDaemons = useDaemons(token, { enabled: can('runners.read') });
  const sharedDaemons = useSharedDaemons(token, projectId, { enabled: can('runners.read') });
  const runnerNames = new Map(
    [...(ownDaemons.data?.daemons ?? []), ...(sharedDaemons.data ?? [])].map((item) => [
      item.id,
      item.label ?? item.id,
    ]),
  );
  return (
    <section className="space-y-4">
      {editing !== undefined && canEdit ? (
        <ScheduleForm
          key={editing?.id ?? 'new'}
          token={token}
          projectId={projectId}
          schedule={editing}
          onClose={() => setEditing(undefined)}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold">{t('title')}</h2>
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-[var(--color-fg-muted)]">{t('description')}</p>
            </div>
            {canEdit && (
              <button type="button" className="btn btn-primary btn-sm shrink-0" onClick={() => setEditing(null)}>
                {t('addSchedule')}
              </button>
            )}
          </div>
          {list.isLoading ? (
            <Spinner />
          ) : list.isError ? (
            <p role="alert">
              {t('loadFailed')}{' '}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => void list.refetch()}>
                {c('actions.retry')}
              </button>
            </p>
          ) : (
            <>
              {list.data?.data.length === 0 && (
                <p className="py-6 text-sm text-[var(--color-fg-muted)]">{t('empty')}</p>
              )}
              <ul className="space-y-2">
                {list.data?.data.map((schedule) => (
                  <ScheduleRow
                    key={schedule.id}
                    schedule={schedule}
                    token={token}
                    canEdit={canEdit}
                    agentName={agentNames.get(schedule.agentConfigId)}
                    runnerNames={runnerNames}
                    onEdit={() => setEditing(schedule)}
                    onDelete={() => setDeleting(schedule)}
                  />
                ))}
              </ul>
              {(list.data?.meta.totalPages ?? 0) > 1 && (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    {c('actions.prev')}
                  </button>
                  <span>
                    {c('pagination.pageInfo', {
                      total: list.data?.meta.total ?? 0,
                      page,
                      pages: list.data?.meta.totalPages ?? 1,
                    })}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={page >= (list.data?.meta.totalPages ?? 1)}
                    onClick={() => setPage(page + 1)}
                  >
                    {c('actions.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
      <DeleteConfirmModal
        isOpen={deleting !== null && canEdit}
        title={t('deleteTitle')}
        message={t('deleteMessage', { title: deleting?.title ?? '' })}
        confirmLabel={c('actions.delete')}
        cancelLabel={c('actions.cancel')}
        isPending={remove.isPending}
        zIndex="overlay"
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate(deleting.id, {
            onSuccess: () => {
              setDeleting(null);
              setPage(1);
            },
            onError: () => toast.error(t('deleteFailed')),
          });
        }}
      />
    </section>
  );
}
